import axios, { isAxiosError } from 'axios';

import axiosInstance from 'src/config/axios.config';
import { API_ENDPOINTS } from 'src/config/api.config';

import { CompanyProps } from 'src/types/company';

// Company interface
export interface CompanyDto {
  id: string;
  name: string;
  email: string;
  phone: string;
  address: string;
  website?: string;
  logoUrl?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  description?: string;
}

export interface CreateCompanyDto {
  name: string;
  email: string;
  phone: string;
  address: string;
  website?: string;
  logoUrl?: string;
  description?: string;
}

export interface UpdateCompanyDto extends Partial<CreateCompanyDto> {
  isActive?: boolean;
}

export interface Company {
  id: string;
  name: string;
  address: string;
  phone: string;
  email: string;
  createdAt: string;
  updatedAt: string;
}

export interface CompanyRequest {
  name: string;
  address: string;
  phone: string;
  email: string;
}

export interface PageResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
  first: boolean;
  last: boolean;
  empty: boolean;
}

// Company API fonksiyonları
export const companyApi = {
  // Tüm firmaları getir
  getAll: async (page = 0, size = 10, sortBy = 'name', sortDirection = 'asc'): Promise<PageResponse<CompanyDto>> => {
    try {
      const response = await axiosInstance.get<PageResponse<CompanyDto>>(`${API_ENDPOINTS.COMPANIES}`, {
        params: {
          page,
          size,
          sortBy,
          sortDirection
        }
      });
      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        if (error.response?.status === 403) {
          throw new Error('Bu işlem için SUPER_ADMIN yetkisi gerekiyor');
        }
        throw new Error(error.response?.data?.message || 'Şirketler getirilirken bir hata oluştu');
      }
      throw error;
    }
  },

  // Basit firma listesi (dropdown için) - rolleme bazlı
  getSimpleList: async (): Promise<{ id: string; name: string }[]> => {
    try {
      // Önce SUPER_ADMIN endpoint'ini dene
      const response = await axiosInstance.get<CompanyDto[]>(`${API_ENDPOINTS.COMPANIES}/simple-list`);
      return response.data;
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 403) {
        // SUPER_ADMIN değilse, pagination endpoint'ini dene
        try {
          const response = await axiosInstance.get<PageResponse<CompanyDto>>(`${API_ENDPOINTS.COMPANIES}`, {
            params: { page: 0, size: 100 }
          });
          return response.data.content.map(company => ({
            id: company.id,
            name: company.name
          }));
        } catch (paginationError) {
          // Hiçbiri çalışmıyorsa boş array döndür
          console.warn('Companies could not be loaded:', paginationError);
          return [];
        }
      }
      throw error;
    }
  },

  // Rolleme bazlı firma listesi (kullanıcının rolüne göre)
  getCompaniesForCurrentUser: async (): Promise<{ id: string; name: string }[]> => {
    try {
      // Kullanıcının erişebileceği şirketleri getir
      const response = await axiosInstance.get<{ id: string; name: string }[]>(`${API_ENDPOINTS.COMPANIES}/for-current-user`);
      return response.data;
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 404) {
        // Endpoint yoksa fallback
        return companyApi.getSimpleList();
      }
      throw error;
    }
  },

  // Firma detaylarını getir
  getById: async (id: string): Promise<CompanyDto> => {
    try {
      const response = await axiosInstance.get<CompanyDto>(`${API_ENDPOINTS.COMPANIES}/${id}`);
      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        if (error.response?.status === 403) {
          throw new Error('Bu işlem için SUPER_ADMIN yetkisi gerekiyor');
        }
        throw new Error(error.response?.data?.message || 'Şirket getirilirken bir hata oluştu');
      }
      throw error;
    }
  },

  // Yeni firma oluştur
  create: async (data: CreateCompanyDto): Promise<CompanyDto> => {
    try {
      const response = await axiosInstance.post<CompanyDto>(`${API_ENDPOINTS.COMPANIES}`, data);
      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        if (error.response?.status === 403) {
          throw new Error('Bu işlem için SUPER_ADMIN yetkisi gerekiyor');
        }
        throw new Error(error.response?.data?.message || 'Şirket oluşturulurken bir hata oluştu');
      }
      throw error;
    }
  },

  // Firma güncelle
  update: async (id: string, data: UpdateCompanyDto): Promise<CompanyDto> => {
    try {
      const response = await axiosInstance.put<CompanyDto>(`${API_ENDPOINTS.COMPANIES}/${id}`, data);
      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        if (error.response?.status === 403) {
          throw new Error('Bu işlem için SUPER_ADMIN yetkisi gerekiyor');
        }
        throw new Error(error.response?.data?.message || 'Şirket güncellenirken bir hata oluştu');
      }
      throw error;
    }
  },

  // Firma sil
  delete: async (id: string): Promise<void> => {
    try {
      await axiosInstance.delete(`${API_ENDPOINTS.COMPANIES}/${id}`);
    } catch (error) {
      if (isAxiosError(error)) {
        if (error.response?.status === 403) {
          throw new Error('Bu işlem için SUPER_ADMIN yetkisi gerekiyor');
        }
        throw new Error(error.response?.data?.message || 'Şirket silinirken bir hata oluştu');
      }
      throw error;
    }
  },

  // Firma aktif/pasif durumunu değiştir
  toggleStatus: async (id: string): Promise<CompanyDto> => {
    const response = await axiosInstance.patch<CompanyDto>(`${API_ENDPOINTS.COMPANIES}/${id}/toggle-status`);
    return response.data;
  }
};

export const getAllCompanies = async (page = 0, size = 5, sortBy = 'name', sortDirection = 'asc', searchName?: string) => {
  try {
    const response = await axiosInstance.get(`${API_ENDPOINTS.COMPANIES}`, {
      params: {
        page,
        size,
        sortBy,
        sortDirection,
        ...(searchName && { searchName }), // Search parametresi varsa ekle
      },
    });
    return response.data;
  } catch (error) {
    if (isAxiosError(error)) {
      if (error.response?.status === 403) {
        throw new Error('Bu işlem için SUPER_ADMIN yetkisi gerekiyor');
      }
      throw new Error(error.response?.data?.message || 'Şirketler getirilirken bir hata oluştu');
    }
    throw error;
  }
};

export const deleteCompany = async (id: string) => {
  try {
    const response = await axiosInstance.delete(`${API_ENDPOINTS.COMPANIES}/${id}`);
    return response.data;
  } catch (error) {
    if (isAxiosError(error)) {
      if (error.response?.status === 403) {
        throw new Error('Bu işlem için SUPER_ADMIN yetkisi gerekiyor');
      }
      throw new Error(error.response?.data?.message || 'Şirket silinirken bir hata oluştu');
    }
    throw error;
  }
}; 